/**
 * RouteCard - Display a generated running route
 */

import { motion } from 'framer-motion';
import type { GeneratedRoute } from '@/types/runPlanner';
import { cn } from '@/utils/cn';

interface RouteCardProps {
  route: GeneratedRoute;
  isSelected: boolean;
  onSelect: () => void;
  onSchedule: () => void;
}

export function RouteCard({ route, isSelected, onSelect, onSchedule }: RouteCardProps) {
  const distanceKm = (route.distance / 1000).toFixed(1);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'p-4 rounded-xl border transition-all cursor-pointer',
        isSelected
          ? 'bg-cyan-500/10 border-cyan-500'
          : 'bg-white/5 border-white/10 hover:border-white/20',
        route.recommended && 'ring-2 ring-emerald-500/50'
      )}
      onClick={onSelect}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-white">{route.name}</h3>
            {route.recommended && (
              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] font-medium rounded-full">
                RECOMMENDED
              </span>
            )}
          </div>
          <p className="text-sm text-gray-400 mt-1">
            {distanceKm} km • ~{route.estimatedDuration.joggingPace} min jog
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-cyan-400">{route.scores.overall}</div>
          <div className="text-[10px] text-gray-400">Overall Score</div>
        </div>
      </div>

      {/* Scores */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <ScoreItem
          label="Air Quality"
          value={route.scores.airQuality}
          color="emerald"
        />
        <ScoreItem
          label="Safety"
          value={route.scores.safety}
          color="blue"
        />
        <ScoreItem
          label="Green Cover"
          value={route.scores.greenCoverage}
          suffix="%"
          color="green"
        />
        <ScoreItem
          label="Brain Boost"
          value={route.scores.neuralIndexImpact}
          suffix="%"
          prefix="+"
          color="violet"
        />
      </div>

      {/* Route Segments Preview */}
      <div className="flex gap-1 mb-4">
        {route.segments.map((segment, index) => (
          <div
            key={index}
            className={cn(
              'h-2 rounded-full',
              segment.type === 'park' && 'bg-green-500',
              segment.type === 'trail' && 'bg-emerald-500',
              segment.type === 'waterfront' && 'bg-cyan-500',
              segment.type === 'sidewalk' && 'bg-gray-500',
              segment.type === 'residential' && 'bg-blue-500',
              segment.type === 'crossing' && 'bg-yellow-500'
            )}
            style={{ flex: segment.distance }}
            title={`${segment.type}: ${(segment.distance / 1000).toFixed(1)}km`}
          />
        ))}
      </div>

      {/* Warnings */}
      {route.warnings.length > 0 && (
        <div className="mb-4 space-y-1">
          {route.warnings.map((warning, index) => (
            <div
              key={index}
              className={cn(
                'flex items-center gap-2 text-xs px-2 py-1 rounded',
                warning.severity === 'high' && 'bg-red-500/20 text-red-400',
                warning.severity === 'medium' && 'bg-yellow-500/20 text-yellow-400',
                warning.severity === 'low' && 'bg-blue-500/20 text-blue-400'
              )}
            >
              <WarningIcon className="w-3 h-3" />
              {warning.message}
            </div>
          ))}
        </div>
      )}

      {/* Waypoints */}
      <div className="flex items-center gap-3 mb-4 text-xs text-gray-400">
        {route.waypoints.filter(w => w.type !== 'start').slice(0, 3).map((waypoint, index) => (
          <span key={index} className="flex items-center gap-1">
            {waypoint.type === 'water_fountain' && <WaterIcon className="w-3 h-3 text-cyan-400" />}
            {waypoint.type === 'checkpoint' && <FlagIcon className="w-3 h-3 text-violet-400" />}
            {waypoint.type === 'landmark' && <StarIcon className="w-3 h-3 text-yellow-400" />}
            {waypoint.type === 'crossing' && <CrossingIcon className="w-3 h-3 text-orange-400" />}
            {waypoint.name}
          </span>
        ))}
      </div>

      {/* Forecast */}
      {route.forecast && (
        <div className="flex items-center gap-4 p-2 bg-white/5 rounded-lg mb-4 text-xs">
          <span className="text-gray-400">
            <ThermometerIcon className="w-3 h-3 inline mr-1" />
            {route.forecast.temperature}°C
          </span>
          <span className="text-gray-400">
            <DropletIcon className="w-3 h-3 inline mr-1" />
            {route.forecast.humidity}%
          </span>
          <span className="text-emerald-400">
            AQI {route.forecast.airQualityIndex}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSchedule();
          }}
          className="flex-1 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-black font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(0,255,213,0.3)] transition-all"
        >
          Schedule This Run
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Share functionality
          }}
          className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
        >
          <ShareIcon className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

// Score Item Component
function ScoreItem({
  label,
  value,
  prefix = '',
  suffix = '',
  color,
}: {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  color: 'emerald' | 'blue' | 'green' | 'violet';
}) {
  const colorClasses = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    green: 'text-green-400',
    violet: 'text-violet-400',
  };

  return (
    <div className="text-center">
      <div className={cn('text-lg font-bold', colorClasses[color])}>
        {prefix}{typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(1)) : value}{suffix}
      </div>
      <div className="text-[10px] text-gray-500">{label}</div>
    </div>
  );
}

// Icons
function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function WaterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z" />
    </svg>
  );
}

function FlagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function CrossingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );
}

function ThermometerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function DropletIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
    </svg>
  );
}

export default RouteCard;
